/**
 * The Appointments list's status pill and row actions, from a booking, the
 * branch's clock and the organisation's check-in window.
 */
import { normaliseCode } from './appointmentCode'
import type { Appointment, CheckInRules } from './types'

export type StatusKind = 'upcoming' | 'early' | 'onTime' | 'lateAllowed' | 'late' | 'noShow' | 'checkedIn' | 'cancelled'
export type StatusTone = 'grey' | 'early' | 'ok' | 'warn' | 'bad'

export interface RowStatus {
  kind: StatusKind
  tone: StatusTone
  /** Whole minutes past the booked time, for 'late'. */
  lateBy?: number
  /** When check-in opens (ms), for a today booking before its window. */
  opensAt?: number
  /** Beyond the tolerance: the minutes allowed, said under the pill. */
  beyond?: number
  checkIn: boolean
  /** Only a regular ticket is left: the booking is past its late tolerance. */
  regular: boolean
}

const MIN = 60_000

/**
 * today: the booking's day is the branch's business day. Without rules (a
 * server before the check-in window) check-in is open all day.
 */
export function rowStatus(a: Appointment, now: number, today: boolean, rules?: CheckInRules): RowStatus {
  const none = { checkIn: false, regular: false }
  if (a.state === 'arrived') return { kind: 'checkedIn', tone: 'ok', ...none }
  if (a.state === 'cancelled') return { kind: 'cancelled', tone: 'grey', ...none }
  if (a.state === 'missed') return { kind: 'noShow', tone: 'bad', ...none }
  if (!today) return { kind: 'upcoming', tone: 'grey', ...none }

  const at = Date.parse(a.startsAt)
  const d = now - at
  const lateBy = Math.floor(d / MIN)
  const open = { checkIn: true, regular: false }
  if (!rules) {
    if (d < 0) return { kind: 'early', tone: 'early', ...open }
    if (d < MIN) return { kind: 'onTime', tone: 'ok', ...open }
    return { kind: 'late', tone: 'warn', lateBy, ...open }
  }
  if (d < -rules.earlyMin * MIN) return { kind: 'upcoming', tone: 'grey', opensAt: at - rules.earlyMin * MIN, ...none }
  if (d > rules.lateMin * MIN) return { kind: 'late', tone: 'bad', lateBy, beyond: rules.lateMin, checkIn: false, regular: true }
  if (Math.abs(d) <= rules.onTimeMin * MIN) return { kind: 'onTime', tone: 'ok', ...open }
  if (d < 0) return { kind: 'early', tone: 'early', ...open }
  return { kind: 'lateAllowed', tone: 'warn', ...open }
}

const digits = (s: string) => s.replace(/\D/g, '')

/** Search over the loaded list: a name, a phone (spacing ignored), a code or reference, or any declared value. */
export function matchesBooking(a: Appointment, query: string): boolean {
  const q = query.trim().toLocaleLowerCase()
  if (q === '') return true
  const code = normaliseCode(query)
  const d = digits(q)
  const values = [a.reference, a.customerName ?? '', a.contact ?? '', ...Object.values(a.fields ?? {})]
  return (
    (code !== '' && a.code.includes(code)) ||
    values.some((v) => v.toLocaleLowerCase().includes(q)) ||
    (d.length >= 3 && d.length === q.replace(/[\s()+.-]/g, '').length && values.some((v) => digits(v).includes(d)))
  )
}

/** YYYY-MM-DD, n days on, read and written in UTC so no zone moves the day. */
export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T12:00:00Z`)
  d.setUTCDate(d.getUTCDate() + n)
  return d.toISOString().slice(0, 10)
}

/** HH:MM of a moment on the branch's clock, as the time input and the save route take it. */
export function clockIn(iso: string, zone?: string): string {
  return new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: zone }).format(new Date(iso))
}

/** A file-placed booking still booked: changed by saving its reference again (service, day, time). */
export function editable(a: Appointment, seated: boolean): boolean {
  return seated && a.state === 'booked' && a.source === 'import'
}
